import { useEffect, useRef } from "react";
import { live } from "../lib/api";

/**
 * useLivePresence — pings the backend every `intervalMs` ms while
 * `enabled` is true, letting supervisors see the session in the
 * Live Sessions list. Also sends one final "close" beacon on unmount
 * so a session that ends cleanly disappears immediately (otherwise it
 * lingers for 15 s until the sweeper drops it).
 *
 * The heartbeat is deliberately fire-and-forget — network hiccups on
 * cellular tablets must never crash the surveying UI.
 */
export function useLivePresence({
  enabled,
  tool,
  sessionId,
  sessionName,
  username,
  role,
  counters,
  intervalMs = 5000,
}) {
  // Keep the newest counters/name in a ref so the beat loop always
  // sends fresh data without restarting the interval.
  const dataRef = useRef({ sessionName, username, role, counters });
  dataRef.current = { sessionName, username, role, counters };

  useEffect(() => {
    if (!enabled || !tool || !sessionId) return undefined;

    const beat = () => {
      const { sessionName, username, role, counters } = dataRef.current;
      live
        .heartbeat({
          tool,
          session_id: sessionId,
          session_name: sessionName || "",
          username: username || "",
          role: role || "operator",
          counters: counters || {},
        })
        .catch(() => undefined);
    };
    beat();
    const id = setInterval(beat, intervalMs);
    return () => {
      clearInterval(id);
      live.close(tool, sessionId).catch(() => undefined);
    };
  }, [enabled, tool, sessionId, intervalMs]);
}
